import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg, fmtDate, dueClass } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Trash2, Satellite, Download, RefreshCw, PoundSterling, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  registration: "", make: "", model: "", colour: "", fuel_type: "", year: "",
  tyre_size_front: "", tyre_size_rear: "", driver_id: "", has_tail_lift: false, has_fridge: false,
  mot_expiry: "", tax_due: "", service_due: "", inspection_due: "", tail_lift_expiry: "", fridge_expiry: "",
};

const F = ({ label, children }) => (
  <div>
    <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const inputCls = "rounded-none bg-background border-border h-9 text-sm";

export default function VehiclesPage() {
  const nav = useNavigate();
  const { canEdit } = useAuth();
  const editable = canEdit("vehicles");
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [dvla, setDvla] = useState(null);
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [progress, setProgress] = useState(null);
  const [refreshSettings, setRefreshSettings] = useState(null);

  const load = () => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => {});
    api.get("/vehicles/refresh-settings").then((r) => setRefreshSettings(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const toggleAuto = async (enabled) => {
    try {
      const { data } = await api.put("/vehicles/refresh-settings", { auto_enabled: enabled });
      setRefreshSettings(data);
      toast.success(enabled ? "Auto weekly refresh turned on" : "Auto weekly refresh turned off");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lookup = async () => {
    if (!form.registration.trim()) return toast.error("Enter a registration first");
    setLooking(true);
    try {
      const { data } = await api.post("/dvla/lookup", { registration: form.registration });
      setDvla(data);
      setForm((f) => ({
        ...f,
        make: data.make || f.make,
        model: data.model || f.model,
        colour: data.colour || f.colour,
        fuel_type: data.fuelType || f.fuel_type,
        year: data.yearOfManufacture || f.year,
        mot_expiry: data.motExpiryDate || f.mot_expiry,
        tax_due: data.taxDueDate || f.tax_due,
      }));
      toast.success(data.source === "mock" ? "Lookup unavailable right now — using placeholder data" : data.source === "dvsa" ? "Official DVSA data fetched" : (data.source === "web" || data.source === "govuk") ? "Live vehicle data fetched" : "DVLA data fetched");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLooking(false);
    }
  };

  const save = async () => {
    if (!form.registration.trim()) return toast.error("Registration is required");
    setSaving(true);
    try {
      const payload = { ...form, year: form.year ? Number(form.year) : null };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (dvla) { payload.dvla_data = dvla; payload.dvla_source = dvla.source; }
      await api.post("/vehicles", payload);
      toast.success(`Vehicle ${form.registration.toUpperCase()} added`);
      setOpen(false);
      setForm(EMPTY);
      setDvla(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/export/fleet.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "fleet_export.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Fleet spreadsheet downloaded");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const pollRefresh = async () => {
    try {
      const { data } = await api.get("/vehicles/refresh-all/status");
      setProgress(data);
      if (data.running) {
        setTimeout(pollRefresh, 2000);
      } else {
        setRefreshing(false);
        setProgress(null);
        toast.success(`Fleet refreshed — ${data.updated} of ${data.total} vehicles updated${data.failed.length ? `. No data found for: ${data.failed.join(", ")}` : ""}`);
        load();
      }
    } catch (e) {
      setRefreshing(false);
      setProgress(null);
      toast.error(errMsg(e));
    }
  };

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      await api.post("/vehicles/refresh-all");
      setTimeout(pollRefresh, 1500);
    } catch (e) {
      setRefreshing(false);
      toast.error(errMsg(e));
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete vehicle ${v.registration} and all its records?`)) return;
    await api.delete(`/vehicles/${v.id}`);
    toast.success("Vehicle deleted");
    load();
  };

  const shown = vehicles.filter((v) =>
    [v.registration, v.make, v.model, v.driver_name].join(" ").toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="p-8" data-testid="vehicles-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">VEHICLES</h1>
          <p className="text-muted-foreground text-sm mt-1">{vehicles.length} vehicles in fleet</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="vehicle-search-input" placeholder="Search reg, make, driver…" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-none bg-card border-border pl-9 w-64 h-10" />
          </div>
          <Button data-testid="companies-view-button" onClick={() => nav("/companies")} variant="outline" className="rounded-none border-border hover:bg-muted hover:text-foreground text-foreground bg-transparent font-bold uppercase tracking-widest text-xs h-10">
            <Building2 size={15} className="mr-1" /> Companies
          </Button>
          <Button data-testid="charges-view-button" onClick={() => nav("/charges")} variant="outline" className="rounded-none border-border hover:bg-muted hover:text-foreground text-foreground bg-transparent font-bold uppercase tracking-widest text-xs h-10">
            <PoundSterling size={15} className="mr-1" /> Charge zones
          </Button>
          {editable && (
          <Button data-testid="refresh-fleet-button" onClick={refreshAll} disabled={refreshing} variant="outline" className="rounded-none border-border hover:bg-muted hover:text-foreground text-foreground bg-transparent font-bold uppercase tracking-widest text-xs h-10">
            <RefreshCw size={15} className={`mr-1 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? (progress?.total ? `Refreshing ${progress.processed}/${progress.total}…` : "Refreshing…") : "Refresh fleet data"}
          </Button>
          )}
          <Button data-testid="export-fleet-button" onClick={exportCsv} variant="outline" className="rounded-none border-border hover:bg-muted hover:text-foreground text-foreground bg-transparent font-bold uppercase tracking-widest text-xs h-10">
            <Download size={15} className="mr-1" /> Export CSV
          </Button>
          {editable && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-vehicle-button" className="rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-10">
                <Plus size={15} className="mr-1" /> Add vehicle
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border rounded-none text-foreground max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading font-black tracking-tighter text-2xl">ADD VEHICLE</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <F label="Registration number *">
                      <Input data-testid="vehicle-reg-input" value={form.registration} onChange={(e) => set("registration", e.target.value.toUpperCase())} placeholder="AB12 CDE" className={`${inputCls} font-mono font-bold`} />
                    </F>
                  </div>
                  <Button data-testid="dvla-lookup-button" onClick={lookup} disabled={looking} variant="outline" className="rounded-none border-amber-600/50 dark:border-[#FFD60A]/50 text-amber-600 dark:text-[#FFD60A] hover:bg-amber-600/10 dark:hover:bg-amber-600/10 dark:bg-[#FFD60A]/10 hover:text-amber-600 dark:hover:text-amber-600 dark:text-[#FFD60A] h-9 uppercase text-xs tracking-widest font-bold bg-transparent">
                    <Satellite size={14} className="mr-1" /> {looking ? "Fetching…" : "DVLA lookup"}
                  </Button>
                </div>
                {dvla && (
                  <div data-testid="dvla-result-panel" className={`border px-3 py-2 text-xs ${dvla.source === "mock" ? "border-amber-600/40 dark:border-[#FFD60A]/40 bg-amber-600/5 dark:bg-[#FFD60A]/5 text-amber-600 dark:text-[#FFD60A]" : "border-emerald-600/40 dark:border-[#32D74B]/40 bg-emerald-600/5 dark:bg-[#32D74B]/5 text-emerald-600 dark:text-[#32D74B]"}`}>
                    {dvla.source === "mock" ? "MOCK DATA" : dvla.source === "dvsa" ? "OFFICIAL DVSA DATA" : (dvla.source === "web" || dvla.source === "govuk") ? "LIVE VEHICLE DATA" : "LIVE DVLA DATA"} — {dvla.make} {dvla.model || ""} · {dvla.colour} · {dvla.fuelType} · MOT {dvla.motStatus} until {fmtDate(dvla.motExpiryDate)} · Tax {dvla.taxStatus}{dvla.safetyRecall ? " · SAFETY RECALL OUTSTANDING" : ""}
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <F label="Make"><Input data-testid="vehicle-make-input" value={form.make} onChange={(e) => set("make", e.target.value)} className={inputCls} /></F>
                  <F label="Model"><Input data-testid="vehicle-model-input" value={form.model} onChange={(e) => set("model", e.target.value)} className={inputCls} /></F>
                  <F label="Year"><Input type="number" value={form.year} onChange={(e) => set("year", e.target.value)} className={inputCls} /></F>
                  <F label="Colour"><Input value={form.colour} onChange={(e) => set("colour", e.target.value)} className={inputCls} /></F>
                  <F label="Fuel"><Input value={form.fuel_type} onChange={(e) => set("fuel_type", e.target.value)} className={inputCls} /></F>
                  <F label="Driver">
                    <Select value={form.driver_id || "none"} onValueChange={(v) => set("driver_id", v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="vehicle-driver-select" className={inputCls}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent className="bg-card border-border text-foreground rounded-none">
                        <SelectItem value="none">Unassigned</SelectItem>
                        {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Tyre size front"><Input data-testid="vehicle-tyre-front-input" value={form.tyre_size_front} onChange={(e) => set("tyre_size_front", e.target.value)} placeholder="235/65 R16" className={inputCls} /></F>
                  <F label="Tyre size rear"><Input data-testid="vehicle-tyre-rear-input" value={form.tyre_size_rear} onChange={(e) => set("tyre_size_rear", e.target.value)} placeholder="235/65 R16" className={inputCls} /></F>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <F label="MOT expiry"><Input data-testid="vehicle-mot-input" type="date" value={form.mot_expiry} onChange={(e) => set("mot_expiry", e.target.value)} className={inputCls} /></F>
                  <F label="Tax due"><Input data-testid="vehicle-tax-input" type="date" value={form.tax_due} onChange={(e) => set("tax_due", e.target.value)} className={inputCls} /></F>
                  <F label="Service due"><Input type="date" value={form.service_due} onChange={(e) => set("service_due", e.target.value)} className={inputCls} /></F>
                  <F label="Inspection due"><Input type="date" value={form.inspection_due} onChange={(e) => set("inspection_due", e.target.value)} className={inputCls} /></F>
                </div>
                <div className="grid grid-cols-2 gap-3 border border-border p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground/80">Tail lift fitted</span>
                    <Switch data-testid="vehicle-taillift-switch" checked={form.has_tail_lift} onCheckedChange={(v) => set("has_tail_lift", v)} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground/80">Fridge unit fitted</span>
                    <Switch data-testid="vehicle-fridge-switch" checked={form.has_fridge} onCheckedChange={(v) => set("has_fridge", v)} />
                  </div>
                  {form.has_tail_lift && <F label="Tail lift inspection due"><Input type="date" value={form.tail_lift_expiry} onChange={(e) => set("tail_lift_expiry", e.target.value)} className={inputCls} /></F>}
                  {form.has_fridge && <F label="Fridge inspection due"><Input type="date" value={form.fridge_expiry} onChange={(e) => set("fridge_expiry", e.target.value)} className={inputCls} /></F>}
                </div>
                <Button data-testid="save-vehicle-button" onClick={save} disabled={saving} className="w-full rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-11">
                  {saving ? "Saving…" : "Save vehicle"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {refreshSettings && (
        <div data-testid="auto-refresh-bar" className="border border-border bg-card px-4 py-3 mb-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <RefreshCw size={15} className="text-muted-foreground" />
            <div className="text-sm">
              <span className="font-semibold">Auto weekly refresh</span>
              <span className="text-muted-foreground">
                {" "}— {refreshSettings.auto_enabled ? "vehicle details update automatically every week" : "turned off"}
                {refreshSettings.last_run && ` · Last refreshed ${fmtDate(refreshSettings.last_run.slice(0, 10))}`}
                {refreshSettings.auto_enabled && refreshSettings.next_run && ` · Next ${fmtDate(refreshSettings.next_run.slice(0, 10))}`}
              </span>
            </div>
          </div>
          {editable && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{refreshSettings.auto_enabled ? "On" : "Off"}</span>
              <Switch data-testid="auto-refresh-switch" checked={refreshSettings.auto_enabled} onCheckedChange={toggleAuto} />
            </div>
          )}
        </div>
      )}

      <div className="border border-border bg-card overflow-x-auto overflow-y-auto max-h-[calc(100vh-230px)]">
        <table className="w-full text-sm" data-testid="vehicles-table">
          <thead className="sticky top-0 z-20 bg-card shadow-[0_1px_0_0_hsl(var(--border))]">
            <tr className="text-left">
              {["Reg", "Make / Model", "Company", "Driver", "Tyres F/R", "MOT", "Tax", "Service", "Tail lift", "Fridge", ""].map((h, i) => (
                <th key={i} className="px-4 py-3 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((v) => (
              <tr key={v.id} data-testid={`vehicle-row-${v.registration}`} className="border-b border-border/60 hover:bg-muted/60 cursor-pointer" onClick={() => nav(`/vehicles/${v.id}`)}>
                <td className="px-4 py-3 font-mono font-bold whitespace-nowrap">{v.registration}</td>
                <td className="px-4 py-3 text-foreground/80 whitespace-nowrap">{[v.make, v.model].filter(Boolean).join(" ") || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap" data-testid={`vehicle-company-${v.registration}`}>{v.company_name || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{v.driver_name || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground font-mono text-xs whitespace-nowrap">{v.tyre_size_front || "—"} / {v.tyre_size_rear || "—"}</td>
                <td className={`px-4 py-3 whitespace-nowrap ${dueClass(v.mot_expiry)}`}>{fmtDate(v.mot_expiry)}</td>
                <td className={`px-4 py-3 whitespace-nowrap ${dueClass(v.tax_due)}`}>{fmtDate(v.tax_due)}</td>
                <td className={`px-4 py-3 whitespace-nowrap ${dueClass(v.service_due)}`}>{fmtDate(v.service_due)}</td>
                <td className={`px-4 py-3 whitespace-nowrap ${v.has_tail_lift ? dueClass(v.tail_lift_expiry) : "text-muted-foreground/40"}`}>{v.has_tail_lift ? fmtDate(v.tail_lift_expiry) : "N/A"}</td>
                <td className={`px-4 py-3 whitespace-nowrap ${v.has_fridge ? dueClass(v.fridge_expiry) : "text-muted-foreground/40"}`}>{v.has_fridge ? fmtDate(v.fridge_expiry) : "N/A"}</td>
                <td className="px-4 py-3">
                  {editable && (
                  <button data-testid={`delete-vehicle-${v.registration}`} onClick={(e) => { e.stopPropagation(); remove(v); }} className="text-muted-foreground/70 hover:text-[#FF3B30] transition-colors duration-150">
                    <Trash2 size={15} />
                  </button>
                  )}
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-muted-foreground/70">No vehicles found. Click "Add vehicle" to get started.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
